import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './admin.css';

/**
 * Decodes a JWT payload (without verification) to read claims.
 * Used client-side only for UI gating — actual authorization happens server-side.
 */
function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1];
    if (!base64) return null;
    const payload = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isAdminUser(token: string | null): boolean {
  if (!token) return false;
  const payload = decodeTokenPayload(token);
  if (!payload) return false;

  // Cognito puts groups in 'cognito:groups' claim
  const groups = payload['cognito:groups'];
  if (Array.isArray(groups)) {
    return groups.includes('admin');
  }
  return false;
}

export function AdminLayout() {
  const { user, token, loading } = useAuth();

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  if (!isAdminUser(token)) {
    return (
      <div className="admin-denied">
        <h2>Access Denied</h2>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <h2>Admin</h2>
        <nav>
          <NavLink to="/admin" end>
            Overview
          </NavLink>
          <NavLink to="/admin/brands">Brands</NavLink>
          <NavLink to="/admin/reviews">Reviews</NavLink>
          <NavLink to="/admin/users">Users</NavLink>
          <NavLink to="/admin/scraper">Scraper</NavLink>
          <NavLink to="/admin/affiliates">Affiliates</NavLink>
        </nav>
      </aside>
      <main className="admin-content">
        <Outlet />
      </main>
    </div>
  );
}
